import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  useProjectTemplatesQuery,
  TemplateItem,
  GetTemplatesParams,
  useDuplicateProjectTemplate,
  useDeleteProjectTemplateMutation,
} from "../api/projectManagementApi";
import { toast } from "sonner";
import { X, Edit2, Trash2, Search, FileText } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import paths from "../routes";
import { ref } from "process";
import { useDebounce } from "../hooks/useDebounce";

interface SelectProjectTemplateProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate?: (template: TemplateItem) => void;
  refetch?: any;
}

const SelectProjectTemplate: React.FC<SelectProjectTemplateProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  refetch,
}) => {
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(
    null,
  );

  // Debounce the input value
  const debouncedSearchQuery = useDebounce(inputValue, 1000);

  // Sync searchText with debounced value
  useEffect(() => {
    setSearchText(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const params: GetTemplatesParams = useMemo(
    () => ({
      page: 1,
      limit: 4,
      search: searchText,
    }),
    [searchText],
  );

  const {
    data: templateData,
    isLoading,
    isError,
    error,
  } = useProjectTemplatesQuery(params);
  const navigate = useNavigate();
  // Duplicate template mutation
  const { mutateAsync: duplicateTemplate, isPending: isDuplicating } =
    useDuplicateProjectTemplate();

  // Delete template mutation
  const { mutate: deleteTemplate } = useDeleteProjectTemplateMutation({
    onSuccess: () => {
      toast.success("Template deleted successfully!");
      refetch?.();
      queryClient.invalidateQueries({ queryKey: ["projects", "templates"] });
    },
    onError: (err: any) => {
      refetch?.();
      toast.error(
        err?.response?.data?.data?.message || "Failed to delete template",
      );
    },
  });

  // Memoize handlers
  const handleContinue = useCallback(async () => {
    if (!selectedTemplate) return;

    try {
      const duplicated = await duplicateTemplate(selectedTemplate.id);
      toast.success("Template duplicated successfully!");

      if (onSelectTemplate) {
        onSelectTemplate(duplicated as unknown as TemplateItem);
      }
      refetch?.();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.data?.message || "Failed to duplicate template",
      );
    }
  }, [selectedTemplate, duplicateTemplate, onSelectTemplate, onClose]);

  const handleDelete = useCallback(
    (e: React.MouseEvent, templateId: string, templateName: string) => {
      e.stopPropagation();

      deleteTemplate(templateId);
    },
    [deleteTemplate],
  );

  const handleEdit = useCallback(
    (e: React.MouseEvent, templateName: string) => {
      e.stopPropagation();
      toast.info(`Edit functionality for "${templateName}" coming soon`);
    },
    [],
  );

  // Don't render if not open
  if (!isOpen) return null;

  const templates = templateData?.items || [];

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-gray-400/20" />
      <div
        className="bg-white p-6 rounded-lg max-w-3xl w-full max-h-[85vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Template
          </h2>
          <p className="text-gray-600 text-sm">
            Choose a template to get started quickly with pre-configured
            settings
          </p>
        </div>

        {/* Search Input */}
        <div className="mb-6 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Search size={18} />
          </div>
          <input
            type="text"
            placeholder="Search templates by name..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-bits-blue focus:border-transparent bg-gray-50"
          />
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto mb-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading templates...</div>
            </div>
          )}

          {isError && (
            <div className="flex items-center justify-center py-12">
              <p className="text-red-500">
                Error loading templates: {error?.message}
              </p>
            </div>
          )}

          {!isLoading && !isError && templates.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No templates found</p>
              <p className="text-sm text-gray-400 mt-1">
                {inputValue
                  ? "Try adjusting your search"
                  : "Create your first template to get started"}
              </p>
            </div>
          )}

          {!isLoading && !isError && templates.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {templates.map((template: TemplateItem) => {
                const isSelected = selectedTemplate?.id === template.id;

                return (
                  <div
                    key={template.id}
                    className={`relative group border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected
                        ? "border-bits-blue bg-blue-50 shadow-md"
                        : "border-gray-300 hover:border-gray-400 hover:shadow-sm"
                    }`}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    {/* Template Content */}
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-full flex items-center justify-center ${
                          isSelected ? "bg-bits-blue" : "bg-blue-100"
                        }`}
                      >
                        <FileText
                          size={18}
                          className={
                            isSelected ? "text-white" : "text-bits-blue"
                          }
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {template.template_name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {formatDate(template.createdAt)}
                        </p>
                      </div>

                      {isSelected && (
                        <div className="flex-shrink-0">
                          <div className="h-5 w-5 rounded-full bg-bits-blue flex items-center justify-center">
                            <svg
                              className="h-3 w-3 text-white"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="absolute inset-0 bg-bits-blue bg-opacity-10 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                      <button
                        className="p-3 rounded-full bg-white hover:bg-gray-100 transition-colors"
                        //onClick={(e) => handleEdit(e, template.template_name)}
                        aria-label="Edit template"
                      >
                        <Edit2
                          size={16}
                          className="text-bits-blue"
                          onClick={() => {
                            console.log("reached here");
                            navigate(
                              `${paths.editProject.replace(
                                ":projectId",
                                template.id,
                              )}?mode=template&&action=edit`,
                            );
                          }}
                        />
                      </button>
                      <button
                        className="p-3 rounded-full bg-white hover:bg-gray-100 transition-colors"
                        onClick={(e) =>
                          handleDelete(e, template.id, template.template_name)
                        }
                        aria-label="Delete template"
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 border-t pt-4">
          <button
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 px-4 py-2.5 rounded-lg bg-bits-blue text-white font-medium hover:bg-bits-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handleContinue}
            disabled={!selectedTemplate || isDuplicating}
          >
            {isDuplicating ? "Creating..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectProjectTemplate;
